import { cn } from '@foam/buoy/src/lib/utils';
import { useCallback, useMemo } from 'react';
import { Area, AreaChart, CartesianGrid, XAxis, YAxis } from 'recharts';
import { ContentCardPlatformIcon } from '~/components/ContentCard/components/ContentCardPlatformIcon';
import { IconBox } from '~/components/IconBox';
import { SupportedPlatforms } from '~/constants/platforms';
import { CreatorMetricsTimeline } from '~/hooks/api/useGetCreatorMetricsTimeline';
import { compactNumber } from '~/lib/numberUtils';

import { ChartContainer, ChartTooltip } from '../chart';
import { TooltipContent, TooltipContentProps } from './TooltipContent';
import {
  CHART_COLORS,
  calculateLatestMetrics,
  getVisibleChartColor,
  resolveCssColor,
  transformTimelineToChartData,
} from './utils';
import { computeYAxisTicks } from './yAxisTicks';

export interface GrowthRateChartTheme {
  primary?: string;
  secondary?: string;
  background?: string;
  backgroundAlpha?: string;
  textBase?: string;
}

const DEFAULT_THEME: Required<GrowthRateChartTheme> = {
  primary: CHART_COLORS.primary,
  secondary: CHART_COLORS.primary,
  background: '#ffffff',
  backgroundAlpha: '#dee2e8',
  textBase: '#15191e',
};

interface GrowthRateChartProps {
  /** Social platform type (instagram, tiktok, youtube, snapchat) */
  platform: SupportedPlatforms;
  /** Timeline data from the metrics API */
  metricsTimeline: CreatorMetricsTimeline;
  title?: string | null;
  chartClassName?: string;
  containerClassName?: string;
  showPlatformIcon?: boolean;
  showFollowersCount?: boolean;
  showDateRange?: boolean;
  /** Reduced text and image size */
  compact?: boolean;
  theme?: GrowthRateChartTheme;
}

export const GrowthRateChart = ({
  platform,
  metricsTimeline,
  title = 'Follower growth chart',
  containerClassName = '',
  chartClassName,
  showFollowersCount = true,
  showPlatformIcon = true,
  showDateRange = false,
  compact = false,
  theme,
}: GrowthRateChartProps): JSX.Element | null => {
  const colors = {
    primary: theme?.primary ?? DEFAULT_THEME.primary,
    secondary: theme?.secondary ?? DEFAULT_THEME.secondary,
    background: theme?.background ?? DEFAULT_THEME.background,
    backgroundAlpha: theme?.backgroundAlpha ?? DEFAULT_THEME.backgroundAlpha,
    textBase: theme?.textBase ?? DEFAULT_THEME.textBase,
  };

  // Recharts forwards `fill`/`stroke` on <Area> as raw SVG attributes, and
  // SVG attributes don't evaluate `var(--token)` the way CSS properties do —
  // so resolve token colors before handing them to Recharts. If the branded
  // primary is white, use the secondary chart color so the line stays visible.
  const chartColor = useMemo(
    () =>
      getVisibleChartColor(
        resolveCssColor(colors.primary),
        resolveCssColor(colors.secondary),
      ),
    [colors.primary, colors.secondary],
  );

  const chartData = useMemo(
    () => transformTimelineToChartData(metricsTimeline.timeline),
    [metricsTimeline.timeline],
  );

  const yAxisConfig = useMemo(() => {
    const monthlyData = metricsTimeline.timeline.MONTHLY ?? [];
    const followers = monthlyData
      .map((item) => item.metrics.followers)
      .filter((v): v is number => v != null && v > 0);
    if (followers.length === 0) {
      return computeYAxisTicks(0, 0);
    }
    const minFollowers = Math.min(...followers);
    const maxFollowers = Math.max(...followers);
    return computeYAxisTicks(minFollowers, maxFollowers);
  }, [metricsTimeline.timeline]);

  const latestMetrics = useMemo(
    () => calculateLatestMetrics(metricsTimeline.timeline),
    [metricsTimeline.timeline],
  );

  // Memoize the tooltip content renderer to prevent recreation on each render
  const renderTooltipContent = useCallback(
    ({ active, payload }: TooltipContentProps) => (
      <TooltipContent active={active} payload={payload} />
    ),
    [],
  );

  const monthlyDataLength = metricsTimeline.timeline.MONTHLY?.length ?? 0;
  if (monthlyDataLength === 0) {
    return null;
  }

  const growthRateNumber =
    latestMetrics.growthRate != null ? Number(latestMetrics.growthRate) : null;
  const hasGrowthRate = growthRateNumber != null;
  const hasPositiveGrowth = hasGrowthRate && growthRateNumber > 0;
  const hasNegativeGrowth = hasGrowthRate && growthRateNumber < 0;
  const hasGrowthCount = latestMetrics.growth != null;
  const growthSign = (latestMetrics.growth ?? 0) > 0 ? '+' : '';
  const growthAmount = hasGrowthCount
    ? `${growthSign}${(latestMetrics.growth ?? 0).toLocaleString()}`
    : '—';
  const dateRangeSuffix = showDateRange ? ' in this range' : '';
  const growthLabel = `growth (${growthAmount})${dateRangeSuffix}`;
  const growthRateDisplay = hasGrowthRate
    ? `${latestMetrics.growthRate}%`
    : '—';

  return (
    <div
      className={cn(
        'flex flex-[1_1_400px] flex-col',
        'h-[280px] min-w-[280px] rounded-12 border p-4',
        containerClassName,
      )}
      style={{
        backgroundColor: colors.background,
        borderColor: colors.backgroundAlpha,
      }}
      role="figure"
      aria-label={`Follower growth chart for ${platform}`}
    >
      {title != null && (
        <p
          className={cn(
            'font-root-text-weight-bold',
            compact ? 'text-root-size-body-lg' : 'text-root-size-body-xl',
          )}
          style={{ color: colors.textBase }}
        >
          {title}
        </p>
      )}
      <div className={cn('flex items-center gap-2', title != null && 'mt-2')}>
        {title == null && (
          <p
            className={cn(
              'font-root-text-weight-base text-root-text-color-base text-root-size-body-lg',
            )}
          >
            Follower growth rate:
          </p>
        )}
        {showPlatformIcon && (
          <>
            <IconBox
              icon={<ContentCardPlatformIcon boxSize={4} platform={platform} />}
              width={6}
              height={6}
              aria-hidden="true"
            />
            {showFollowersCount && (
              <>
                <p
                  className={cn(
                    'font-root-text-weight-base text-root-text-color-base',
                    compact
                      ? 'text-root-size-body-md'
                      : 'text-root-size-body-lg',
                  )}
                >
                  <span className="mr-1">
                    {compactNumber(latestMetrics.followers)}
                  </span>
                  <span>followers</span>
                </p>
                <div
                  className="size-1 rounded-12 bg-neutral-200"
                  aria-hidden="true"
                />
              </>
            )}
          </>
        )}
        <p
          className={cn(
            {
              'text-green-500': hasPositiveGrowth,
              'text-red-500': hasNegativeGrowth,
            },
            'text-root-size-body-xl font-root-text-weight-bold',
          )}
          aria-label={`Growth rate: ${
            hasPositiveGrowth
              ? 'positive'
              : hasNegativeGrowth
                ? 'negative'
                : !hasGrowthRate
                  ? 'unavailable'
                  : 'neutral'
          } ${growthRateDisplay}`}
        >
          {hasPositiveGrowth && '+'}
          {growthRateDisplay}
        </p>
        <p
          className={cn(
            'font-root-text-weight-base text-root-text-color-base',
            compact ? 'text-root-size-body-md' : 'text-root-size-body-md',
          )}
        >
          {growthLabel}
        </p>
      </div>

      <ChartContainer
        config={{
          growthRate: {
            label: 'Growth Rate',
            color: colors.primary,
          },
        }}
        className={cn(
          'w-full outline-none [&_*]:outline-none',
          chartClassName ?? 'mt-3 h-[150px]',
          compact &&
            '[&_.recharts-cartesian-grid-horizontal_line:first-child]:[stroke-opacity:0] [&_.recharts-cartesian-grid-horizontal_line:last-child]:[stroke-opacity:0] [&_.recharts-xAxis_.recharts-cartesian-axis-tick_text]:!text-root-size-body-sm [&_.recharts-yAxis_.recharts-cartesian-axis-tick_text]:!text-root-size-body-sm',
        )}
        aria-label={`Chart showing follower count from ${
          chartData[0]?.month ?? 'N/A'
        } to ${chartData[chartData.length - 1]?.month ?? 'N/A'}`}
      >
        <AreaChart
          accessibilityLayer
          data={chartData}
          margin={{
            left: compact ? -12 : -20,
            right: 12,
          }}
        >
          <CartesianGrid vertical={false} />
          <XAxis
            dataKey="month"
            tickLine={false}
            axisLine={false}
            tickMargin={8}
            tickFormatter={(value: string) => value.slice(0, 3)}
            interval="equidistantPreserveStart"
          />
          <YAxis
            tickLine={false}
            axisLine={false}
            tickFormatter={yAxisConfig.tickFormatter}
            ticks={[...yAxisConfig.ticks]}
            tick={{ className: 'text-root-size-body-sm' }}
            width={compact ? 50 : 90}
            domain={[...yAxisConfig.domain]}
            tickMargin={8}
            interval={compact ? 0 : undefined}
            padding={compact ? { top: 8, bottom: 0 } : undefined}
          />
          <ChartTooltip cursor={false} content={renderTooltipContent} />
          <Area
            dataKey="followers"
            type="monotone"
            fill={chartColor}
            fillOpacity={0.4}
            stroke={chartColor}
            baseLine={yAxisConfig.domain[0]}
            connectNulls
          />
        </AreaChart>
      </ChartContainer>
    </div>
  );
};
